// Phase 24A — Shared report filter bar (Zone / SubZone / Role / User / Date range).
// Cascading dropdowns: SubZone list refreshes when Zone changes.
// Presets menu is a separate component (see PresetsMenu.jsx) and is rendered
// alongside the filter bar by the parent page.
import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { SearchableSelect } from "@/components/ui/searchable-select";

const ROLE_OPTIONS = ["SE", "ASM", "RSM", "ZSM", "SALES_HEAD", "MIS", "ACCOUNTS",
                         "MANAGEMENT", "DISTRIBUTOR", "SUPER_ADMIN"];

export default function CommonFilterBar({
  value, onChange,
  showZone = true, showSubZone = true, showRole = false, showUser = false,
  showDateRange = false, showAsOfDate = false, showDistributor = false,
  showSku = false,
  extra = null,
}) {
  const [zones, setZones] = useState([]);
  const [subZones, setSubZones] = useState([]);
  const [users, setUsers] = useState([]);
  const [distributors, setDistributors] = useState([]);
  const [skus, setSkus] = useState([]);

  useEffect(() => {
    api.get("/zones").then(r => setZones(r.data.items || [])).catch(() => {});
    // page_size 500 (the endpoint's max) — with 267+ distributor logins now
    // in the system (all newer than most other users), a page_size of 200
    // sorted "newest first" returned distributors only, so SE/ASM/ZSM never
    // showed up as options at all (same root cause fixed earlier in
    // PJPCalendar.jsx's user picker).
    if (showUser) api.get("/users", { params: { page_size: 500 } })
      .then(r => setUsers(r.data.items || [])).catch(() => {});
    // /skus caps page_size at 100 server-side (unlike /distributors' 2000) —
    // fine for now (well under 100 SKUs total), but this filter will start
    // silently truncating the picker once the catalog grows past that.
    if (showSku) api.get("/skus", { params: { page_size: 100, is_active: true } })
      .then(r => setSkus(r.data.items || [])).catch(() => {});
  }, [showUser, showSku]);

  useEffect(() => {
    if (!showSubZone) return;
    const zid = value.zone_id;
    if (!zid) { setSubZones([]); return; }
    api.get("/geography/sub-zones", { params: { zone_id: zid } })
       .then(r => setSubZones(r.data.items || [])).catch(() => setSubZones([]));
  }, [value.zone_id, showSubZone]);

  // Distributor list narrows by Zone when one's picked, same cascade idea
  // as Sub-Zone — re-fetches whenever the zone filter changes.
  useEffect(() => {
    if (!showDistributor) return;
    api.get("/distributors", { params: { page_size: 2000, zone_id: value.zone_id || undefined } })
       .then(r => setDistributors(r.data.items || [])).catch(() => setDistributors([]));
  }, [value.zone_id, showDistributor]);

  const set = (k, v) => onChange({ ...value, [k]: v === "all" ? "" : v });

  // flex-wrap, not a grid-cols-N with sm:/md:/lg: breakpoints: this bar is
  // reused inside the narrow (~480px) field/manager shell, which is often
  // opened in a full-width desktop browser — Tailwind's breakpoint prefixes
  // key off the *viewport*, not this component's actual rendered width, so
  // e.g. `lg:grid-cols-6` would fire (desktop viewport) even though the
  // visible column is phone-narrow, cramming every filter into one row.
  // flex-wrap reacts to the real available width instead, so it degrades
  // correctly in both places.
  return (
    <div className="flex flex-wrap gap-3 items-end"
         data-testid="common-filter-bar">
      {showZone && (
        <div className="w-[47%] min-w-[140px] flex-1">
          <Label className="text-[11px]">Zone</Label>
          <Select value={value.zone_id || "all"} onValueChange={v => set("zone_id", v)}>
            <SelectTrigger className="h-9" data-testid="filter-zone"><SelectValue placeholder="All zones" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All zones</SelectItem>
              {zones.map(z => <SelectItem key={z.id} value={z.id}>{z.code} — {z.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      )}
      {showSubZone && (
        <div className="w-[47%] min-w-[140px] flex-1">
          <Label className="text-[11px]">Sub-Zone</Label>
          <Select value={value.sub_zone_id || "all"}
                    onValueChange={v => set("sub_zone_id", v)}
                    disabled={!value.zone_id}>
            <SelectTrigger className="h-9" data-testid="filter-subzone">
              <SelectValue placeholder={value.zone_id ? "All sub-zones" : "Pick zone first"} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All sub-zones</SelectItem>
              {subZones.map(sz => <SelectItem key={sz.id} value={sz.id}>{sz.code} — {sz.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      )}
      {showDistributor && (
        <div className="w-[47%] min-w-[140px] flex-1">
          <Label className="text-[11px]">Distributor</Label>
          {/* Name-first + type-to-search — a plain code-first Select made a
              300+ distributor list hard to filter by typing a name. */}
          <SearchableSelect
            value={value.distributor_id || ""}
            onChange={v => onChange({ ...value, distributor_id: v })}
            options={distributors.map(d => ({ id: d.id, code: d.code, name: d.name,
                                                label: `${d.name} (${d.code})` }))}
            placeholder="All distributors"
            testid="filter-distributor"
            className="h-9"
          />
        </div>
      )}
      {showSku && (
        <div className="w-[47%] min-w-[140px] flex-1">
          <Label className="text-[11px]">SKU</Label>
          <SearchableSelect
            value={value.sku_id || ""}
            onChange={v => onChange({ ...value, sku_id: v })}
            options={skus.map(s => ({ id: s.id, code: s.sku_code,
                                        name: s.product_name || s.product_code,
                                        label: `${s.sku_code} — ${s.product_name || s.product_code || ""}` }))}
            placeholder="All SKUs"
            testid="filter-sku"
            className="h-9"
          />
        </div>
      )}
      {showRole && (
        <div className="w-[47%] min-w-[140px] flex-1">
          <Label className="text-[11px]">Role</Label>
          <Select value={value.role_code || "all"} onValueChange={v => set("role_code", v)}>
            <SelectTrigger className="h-9" data-testid="filter-role"><SelectValue placeholder="All roles" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All roles</SelectItem>
              {ROLE_OPTIONS.map(r => <SelectItem key={r} value={r}>{r}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      )}
      {showUser && (
        <div className="w-[47%] min-w-[140px] flex-1">
          <Label className="text-[11px]">User</Label>
          {/* Name-first + type-to-search — same fix as the Distributor
              filter above; a plain Select over 300+ users made finding one
              by name impractical. */}
          <SearchableSelect
            value={value.user_id || ""}
            onChange={v => onChange({ ...value, user_id: v })}
            options={users.map(u => ({ id: u.id, label: `${u.name} · ${u.role_code}` }))}
            placeholder="All users"
            testid="filter-user"
            className="h-9"
          />
        </div>
      )}
      {showDateRange && (
        <>
          <div className="w-[47%] min-w-[140px] flex-1">
            <Label className="text-[11px]">From</Label>
            <Input type="date" className="h-9 w-full" value={value.date_from || ""}
                     data-testid="filter-date-from"
                     onChange={e => onChange({ ...value, date_from: e.target.value })} />
          </div>
          <div className="w-[47%] min-w-[140px] flex-1">
            <Label className="text-[11px]">To</Label>
            <Input type="date" className="h-9 w-full" value={value.date_to || ""}
                     data-testid="filter-date-to"
                     onChange={e => onChange({ ...value, date_to: e.target.value })} />
          </div>
        </>
      )}
      {showAsOfDate && (
        <div className="w-[47%] min-w-[140px] flex-1">
          <Label className="text-[11px]">As-of Date</Label>
          <Input type="date" className="h-9 w-full" value={value.as_of || ""}
                   data-testid="filter-as-of"
                   onChange={e => onChange({ ...value, as_of: e.target.value })} />
        </div>
      )}
      {extra}
    </div>
  );
}
